import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  Download, 
  BarChart3,
  TrendingUp,
  Calendar
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface MissingDataReport {
  total_documents: number;
  documents_with_missing_data: number;
  missing_fields: {
    summary: number;
    citation: number;
    publisher: number;
    organization: number;
    court: number;
    date_of_document: number;
  };
  documents_by_type: {
    [key: string]: number;
  };
}

interface DocumentReport {
  id: number;
  title: string;
  document_type: string;
  missing_fields: string[];
  created_at: string;
  uploader: string;
}

const AdminReports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string>('missing-data');

  const { onProfileUpdate } = useAuth();
  const queryClient = useQueryClient();

  // Listen for profile updates and invalidate user-related queries if needed
  useEffect(() => {
    const unsubscribe = onProfileUpdate(() => {
      // Invalidate user-related queries when profile is updated
      queryClient.invalidateQueries('missingDataReport');
      queryClient.invalidateQueries('documentsWithMissingData');
    });
    
    return unsubscribe;
  }, [onProfileUpdate, queryClient]);

  const { data: missingDataReport } = useQuery<MissingDataReport>(
    'missing-data-report',
    () => api.get('/api/admin/reports/missing-data').then(res => res.data)
  );

  const { data: documentsWithMissingData, isLoading: isLoadingDocuments } = useQuery<DocumentReport[]>(
    'documents-missing-data',
    () => api.get('/api/admin/reports/documents-missing-data').then(res => res.data)
  );

  const reports = [
    {
      id: 'missing-data',
      name: 'Missing Data Report',
      description: 'Documents with incomplete information',
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-100 dark:bg-red-900',
    },
    {
      id: 'document-types',
      name: 'Document Types Analysis',
      description: 'Distribution of document types',
      icon: BarChart3,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900',
    },
    {
      id: 'upload-trends',
      name: 'Upload Trends',
      description: 'Document upload patterns over time',
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900',
    },
    {
      id: 'user-activity',
      name: 'User Activity Report',
      description: 'User engagement and activity metrics',
      icon: Calendar,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900',
    },
  ];

  const getMissingFieldsCount = (document: DocumentReport) => {
    return document.missing_fields.length;
  };

  const getMissingFieldsDisplay = (fields: string[]) => {
    return fields.map(field => field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Reports & Analytics
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          View system reports and analyze data quality
        </p>
      </div>

      {/* Report Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {reports.map((report) => {
          const Icon = report.icon;
          return (
            <button
              key={report.id}
              onClick={() => setSelectedReport(report.id)}
              className={`p-6 rounded-lg border-2 transition-all duration-200 ${
                selectedReport === report.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${report.bgColor}`}>
                  <Icon className={`h-6 w-6 ${report.color}`} />
                </div>
                <div className="ml-4 text-left">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    {report.name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {report.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Missing Data Report */}
      {selectedReport === 'missing-data' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          {missingDataReport && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Total Documents
                    </p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {missingDataReport.total_documents}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Documents with Missing Data
                    </p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {missingDataReport.documents_with_missing_data}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Complete Documents
                    </p>
                    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {missingDataReport.total_documents - missingDataReport.documents_with_missing_data}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Missing Fields Breakdown */}
          {missingDataReport && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Missing Fields Breakdown
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(missingDataReport.missing_fields).map(([field, count]) => (
                  <div key={field} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {field.replace('_', ' ')}
                    </span>
                    <span className="text-lg font-semibold text-red-600">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents with Missing Data */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Documents with Missing Data
                </h2>
                <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center">
                  <Download className="h-4 w-4 mr-2" />
                  Export Report
                </button>
              </div>
            </div>

            {isLoadingDocuments ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading documents...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Document
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Missing Fields
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Uploader
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {documentsWithMissingData?.map((document) => (
                      <tr key={document.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {document.title}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {document.document_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 mr-2">
                              {getMissingFieldsCount(document)}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {getMissingFieldsDisplay(document.missing_fields)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {document.uploader}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(document.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {documentsWithMissingData?.length === 0 && !isLoadingDocuments && (
              <div className="p-6 text-center">
                <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No documents with missing data</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  All documents have complete information.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Document Types Analysis */}
      {selectedReport === 'document-types' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Document Types Analysis
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            This report shows the distribution of document types in the system.
          </p>
          {/* Add chart component here */}
        </div>
      )}

      {/* Upload Trends */}
      {selectedReport === 'upload-trends' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Upload Trends
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            This report shows document upload patterns over time.
          </p>
          {/* Add chart component here */}
        </div>
      )}

      {/* User Activity Report */}
      {selectedReport === 'user-activity' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            User Activity Report
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            This report shows user engagement and activity metrics.
          </p>
          {/* Add chart component here */}
        </div>
      )}
    </div>
  );
};

export default AdminReports;
